import { LatLng, OrderSummary } from "@app/domain";
import { DriverMapMarker, MapMarker } from "../MapView.types";

/**
 * Pure derivation of `OrderSummary`'s route into the pickup/dropoff markers
 * every adapter renders. Split out from the driver marker (see
 * `buildDriverMarker` below) so `hooks/useOrderMapMarkers.ts` can memoize
 * this independently of the driver's live position — these positions never
 * change while `order` is loaded, so the objects it returns should stay
 * referentially stable across driver polls. Each adapter feeds one marker
 * object straight into a `useMemo` keyed on that object (see
 * `MarkerLayer`/`MarkerOverlay` in the adapters) to rebuild that marker's
 * icon only when it actually changes — a stable reference here is what lets
 * a driver location update skip rebuilding every pickup/dropoff icon on the
 * map (see Bug #1/#2 in the camera-refactor brief).
 *
 * Dropoff sequence numbers come from `order.route`'s own order — which is
 * populated in route order by construction (see fixtures) — rather than
 * cross-referencing `order.dropoffs`, so there's exactly one source of
 * truth for "which stop is which number".
 */
export function buildRouteMarkers(order: OrderSummary, isDropoffTracking = false): MapMarker[] {
  const markers: MapMarker[] = [];
  let dropoffSequence = 0;

  for (const point of order.route) {
    if (point.kind === "pickup") {
      markers.push({ id: point.id, kind: "pickup", position: { lat: point.lat, lng: point.lng } });
      continue;
    }

    dropoffSequence += 1;
    markers.push({
      id: point.id,
      kind: "dropoff",
      position: { lat: point.lat, lng: point.lng },
      // Sequence numbers are an Ondemand-only affordance, per spec — and
      // skipped in dropoff-tracking mode, where `route` has already been
      // narrowed to a single dropoff (see `applyDropoffTracking`), so
      // numbering it "1" would read as a route position rather than the
      // placeholder it'd actually be.
      sequence: order.kind === "ondemand" && !isDropoffTracking ? dropoffSequence : undefined,
    });
  }

  return markers;
}

/** The driver's marker, if they currently have a live position — `null`
 * otherwise (no driver on the order, or tracking isn't enabled right now).
 * Kept separate from `buildRouteMarkers` so recomputing this on every
 * driver poll never touches the pickup/dropoff markers' object identity. */
export function buildDriverMarker(order: OrderSummary, driverLocation: LatLng | null): DriverMapMarker | null {
  if (!driverLocation || !order.driver) return null;

  return {
    id: "driver",
    kind: "driver",
    position: driverLocation,
    name: order.driver.name,
    avatarUrl: order.driver.avatarUrl,
  };
}

/** The pickup marker's id, if this order has one — used by `useMarkerEta`
 * to know which marker to anchor the "Pickup at" overlay above. */
export function findPickupMarkerId(order: OrderSummary): string | null {
  return order.route.find((point) => point.kind === "pickup")?.id ?? null;
}

/** The last dropoff marker's id, in route order — used by `useMarkerEta`
 * to anchor the "Finish at" overlay once the order is picked up. */
export function findLastDropoffMarkerId(order: OrderSummary): string | null {
  const dropoffs = order.route.filter((point) => point.kind === "dropoff");
  return dropoffs.length > 0 ? dropoffs[dropoffs.length - 1].id : null;
}
